import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axios-instance";

const STATUS_CONFIG = {
  healthy:  { color: "#22c55e", bg: "#dcfce7", label: "Healthy" },
  warning:  { color: "#f59e0b", bg: "#fef3c7", label: "Warning" },
  critical: { color: "#ef4444", bg: "#fee2e2", label: "Critical" },
};

const BUCKET_ICONS = {
  booking_failures:                  "📅",
  calendar_connection_failures:      "🔗",
  calendar_conflict_fetch_failures:  "📆",
  stripe_webhook_failures:           "💳",
  payment_failures:                  "💰",
  email_notification_failures:       "✉️",
  production_500s:                   "🔥",
  failed_jobs:                       "⚙️",
};

const todayDate = () => new Date().toISOString().slice(0, 10);

const LaunchHealth = () => {
  const [date, setDate]         = useState(todayDate());
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchReport = useCallback((reportDate) => {
    setLoading(true);
    setError(null);
    setData(null);
    setExpanded({});

    axiosInstance
      .get(`super-admin/launch-health?date=${reportDate}`)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to load health report."
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchReport(date);
  }, []);

  const handleDateChange = (e) => {
    setDate(e.target.value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReport(date);
  };

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const statusCfg = data ? STATUS_CONFIG[data.status] || STATUS_CONFIG.healthy : null;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Launch Health Report</h1>
          <p style={styles.subtitle}>Daily system health across all launch-critical flows</p>
        </div>
        <form onSubmit={handleSearch} style={styles.dateForm}>
          <input
            type="date"
            value={date}
            onChange={handleDateChange}
            style={styles.dateInput}
            max={todayDate()}
          />
          <button type="submit" style={styles.searchBtn} disabled={loading}>
            {loading ? "Loading..." : "View Report"}
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <span>Loading health report for {date}…</span>
        </div>
      )}

      {/* Report */}
      {data && !loading && (
        <>
          {/* Status banner */}
          <div style={{ ...styles.statusBanner, background: statusCfg.bg, borderColor: statusCfg.color }}>
            <div style={{ ...styles.statusDot, background: statusCfg.color }} />
            <span style={{ ...styles.statusLabel, color: statusCfg.color }}>
              {statusCfg.label}
            </span>
            <span style={styles.statusMeta}>
              &nbsp;—&nbsp;{data.date}&nbsp;&nbsp;|&nbsp;&nbsp;
              {data.total_issues} total issue{data.total_issues !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Bucket cards grid */}
          <div style={styles.grid}>
            {data.buckets &&
              Object.entries(data.buckets).map(([key, bucket]) => {
                const hasIssues = bucket.count > 0;
                const isOpen    = expanded[key];
                return (
                  <div key={key} style={{ ...styles.card, borderColor: hasIssues ? "#ef4444" : "#e5e7eb" }}>
                    <div style={styles.cardTop}>
                      <div style={styles.cardLeft}>
                        <span style={styles.cardIcon}>{BUCKET_ICONS[key] || "📋"}</span>
                        <span style={styles.cardLabel}>{bucket.label}</span>
                      </div>
                      <div style={{ ...styles.countBadge, background: hasIssues ? "#fee2e2" : "#f0fdf4", color: hasIssues ? "#ef4444" : "#22c55e" }}>
                        {bucket.count}
                      </div>
                    </div>

                    {hasIssues && bucket.recent && bucket.recent.length > 0 && (
                      <button
                        style={styles.expandBtn}
                        onClick={() => toggleExpanded(key)}
                      >
                        {isOpen ? "Hide recent ▲" : `Show ${bucket.recent.length} recent ▼`}
                      </button>
                    )}

                    {isOpen && bucket.recent && bucket.recent.length > 0 && (
                      <div style={styles.recentList}>
                        {bucket.recent.map((item, idx) => (
                          <RecentItem key={idx} item={item} bucketKey={key} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

const RecentItem = ({ item, bucketKey }) => {
  const isFailedJob = bucketKey === "failed_jobs";
  const time = item.created_at || item.failed_at
    ? new Date(item.created_at || item.failed_at).toLocaleTimeString()
    : "-";

  return (
    <div style={styles.recentItem}>
      <div style={styles.recentMeta}>
        <span style={styles.recentTime}>{time}</span>
        {isFailedJob ? (
          <span style={styles.recentEvent}>{item.queue || "-"}</span>
        ) : (
          <>
            <span style={styles.recentFlow}>{item.flow}</span>
            <span style={styles.recentEvent}>{item.event_type}</span>
            {item.business_id && (
              <span style={styles.recentBiz}>biz #{item.business_id}</span>
            )}
            {item.booking_id && (
              <span style={styles.recentBiz}>booking #{item.booking_id}</span>
            )}
          </>
        )}
      </div>
      {(item.message || item.exception) && (
        <div style={styles.recentMsg}>
          {(item.message || item.exception || "").slice(0, 150)}
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "32px 24px",
    maxWidth: 1100,
    margin: "0 auto",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 28,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    margin: 0,
    color: "#111827",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#6b7280",
    fontSize: 14,
  },
  dateForm: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  dateInput: {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "7px 12px",
    fontSize: 14,
    color: "#111827",
    outline: "none",
  },
  searchBtn: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBox: {
    background: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fca5a5",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 14,
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: "#6b7280",
    padding: "32px 0",
    fontSize: 15,
  },
  spinner: {
    width: 22,
    height: 22,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #4f46e5",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  statusBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    border: "1px solid",
    borderRadius: 10,
    padding: "14px 20px",
    marginBottom: 24,
    fontSize: 15,
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statusLabel: {
    fontWeight: 700,
    fontSize: 16,
  },
  statusMeta: {
    color: "#374151",
    fontSize: 14,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
  },
  card: {
    background: "#fff",
    border: "1.5px solid",
    borderRadius: 10,
    padding: "18px 18px 14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  cardLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardIcon: {
    fontSize: 20,
  },
  cardLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: "#111827",
  },
  countBadge: {
    fontWeight: 700,
    fontSize: 18,
    borderRadius: 8,
    padding: "2px 12px",
    minWidth: 36,
    textAlign: "center",
  },
  expandBtn: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    padding: "2px 0",
    marginTop: 2,
  },
  recentList: {
    marginTop: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  recentItem: {
    background: "#f9fafb",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 12,
    borderLeft: "3px solid #ef4444",
  },
  recentMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    marginBottom: 3,
  },
  recentTime: {
    color: "#6b7280",
    fontVariantNumeric: "tabular-nums",
  },
  recentFlow: {
    background: "#dbeafe",
    color: "#1d4ed8",
    borderRadius: 4,
    padding: "1px 6px",
    fontWeight: 600,
  },
  recentEvent: {
    background: "#ede9fe",
    color: "#6d28d9",
    borderRadius: 4,
    padding: "1px 6px",
  },
  recentBiz: {
    color: "#9ca3af",
    fontSize: 11,
  },
  recentMsg: {
    color: "#374151",
    fontSize: 11,
    marginTop: 2,
    wordBreak: "break-word",
  },
};

export default LaunchHealth;
